import React, { useState } from 'react'
import { Music, Plus, ArrowRight, Loader2 } from 'lucide-react'
import { Button } from '~/ui/button'
import { Card, CardContent, CardDescription, CardTitle } from '~/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '~/ui/dialog'
import { Badge } from '~/ui/badge'
import { Input } from '~/ui/input'
import { ScrollArea } from '~/ui/scroll-area'

interface SpotifyPlaylist {
  spotifyId: string
  name: string
  description?: string | null
  tracksTotal: number
  public: boolean
  collaborative: boolean
  images?: Array<{
    url: string
    width?: number | null
    height?: number | null
  }>
  owner?: {
    spotifyId: string
    displayName?: string | null
  } | null
}

interface SpotifyImportOptionProps {
  isSpotifyConnected: boolean
  availablePlaylists: SpotifyPlaylist[]
  isLoadingPlaylists: boolean
  onConnectSpotify: () => void
  onImportPlaylist: (playlistId: string, customName?: string) => Promise<void>
  onRefreshPlaylists: () => void
  isImporting?: boolean
  importingPlaylistId?: string | null
}

export const SpotifyImportOption: React.FC<SpotifyImportOptionProps> = ({
  isSpotifyConnected,
  availablePlaylists,
  isLoadingPlaylists,
  onConnectSpotify,
  onImportPlaylist,
  onRefreshPlaylists,
  isImporting = false,
  importingPlaylistId = null,
}) => {
  const [showPlaylistSelector, setShowPlaylistSelector] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedPlaylist, setSelectedPlaylist] = useState<SpotifyPlaylist | null>(null)
  const [customName, setCustomName] = useState('')

  const filteredPlaylists = availablePlaylists.filter(
    (playlist) =>
      playlist.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      playlist.description?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const getPlaylistImage = (playlist: SpotifyPlaylist) => {
    return playlist.images?.[0]?.url || `https://picsum.photos/64/64?random=${playlist.spotifyId}`
  }

  const handleImport = async (playlist: SpotifyPlaylist) => {
    try {
      await onImportPlaylist(playlist.spotifyId, customName.trim() || undefined)
      setSelectedPlaylist(null)
      setCustomName('')
      setShowPlaylistSelector(false)
    } catch (error) {
      console.error('Import failed:', error)
    }
  }

  if (!isSpotifyConnected) {
    return (
      <Card className="border-muted-foreground/25 hover:border-muted-foreground/50 border-2 border-dashed transition-colors">
        <CardContent className="flex flex-col items-center justify-center py-8 text-center">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <Music className="h-6 w-6 text-green-600" />
          </div>
          <CardTitle className="mb-2 text-lg">Import from Spotify</CardTitle>
          <CardDescription className="mb-4 max-w-sm">
            Connect your Spotify account to import your existing playlists
          </CardDescription>
          <Button onClick={onConnectSpotify} className="bg-green-600 hover:bg-green-700">
            <Music className="mr-2 h-4 w-4" />
            Connect Spotify
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card className="cursor-pointer border-green-200 bg-green-50/50 transition-colors hover:bg-green-50">
        <CardContent className="flex flex-col items-center justify-center py-8 text-center">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <Music className="h-6 w-6 text-green-600" />
          </div>
          <CardTitle className="mb-2 text-lg">Import from Spotify</CardTitle>
          <CardDescription className="mb-4 max-w-sm">
            Choose from your {availablePlaylists.length} available Spotify playlists
          </CardDescription>
          <Button
            onClick={() => setShowPlaylistSelector(true)}
            disabled={isLoadingPlaylists || availablePlaylists.length === 0}
            className="bg-green-600 hover:bg-green-700"
          >
            {isLoadingPlaylists ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : availablePlaylists.length === 0 ? (
              'No Playlists Available'
            ) : (
              <>
                <Plus className="mr-2 h-4 w-4" />
                Import Playlist
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Playlist Selector Dialog */}
      <Dialog open={showPlaylistSelector} onOpenChange={setShowPlaylistSelector}>
        <DialogContent className="max-h-[80vh] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Music className="h-5 w-5 text-green-600" />
              <span>Import from Spotify</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Search */}
            <div className="flex items-center space-x-2">
              <Input
                placeholder="Search playlists..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={onRefreshPlaylists}
                disabled={isLoadingPlaylists}
              >
                {isLoadingPlaylists ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
              </Button>
            </div>

            {/* Results count */}
            <div className="text-muted-foreground flex items-center justify-between text-sm">
              <span>{filteredPlaylists.length} playlists available</span>
              <Badge variant="secondary" className="text-xs">
                Connected to Spotify
              </Badge>
            </div>

            {/* Playlist List */}
            <ScrollArea className="h-[400px]">
              <div className="space-y-2">
                {isLoadingPlaylists ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <div
                      key={i}
                      className="bg-muted flex animate-pulse items-center space-x-3 rounded-lg p-3"
                    >
                      <div className="bg-muted-foreground/20 h-12 w-12 rounded" />
                      <div className="flex-1 space-y-2">
                        <div className="bg-muted-foreground/20 h-4 w-3/4 rounded" />
                        <div className="bg-muted-foreground/20 h-3 w-1/2 rounded" />
                      </div>
                    </div>
                  ))
                ) : filteredPlaylists.length === 0 ? (
                  <div className="text-muted-foreground py-8 text-center">
                    <Music className="mx-auto mb-2 h-8 w-8 opacity-50" />
                    <p>No playlists found</p>
                    {searchQuery && <p className="text-sm">Try adjusting your search terms</p>}
                  </div>
                ) : (
                  filteredPlaylists.map((playlist) => {
                    const isCurrentlyImporting =
                      isImporting && importingPlaylistId === playlist.spotifyId

                    return (
                      <div
                        key={playlist.spotifyId}
                        className="hover:bg-muted/50 group flex cursor-pointer items-center space-x-3 rounded-lg border p-3 transition-colors"
                        onClick={() => setSelectedPlaylist(playlist)}
                      >
                        <img
                          src={getPlaylistImage(playlist)}
                          alt={playlist.name}
                          className="h-12 w-12 rounded-lg object-cover"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center space-x-2">
                            <h3 className="line-clamp-1 font-medium">{playlist.name}</h3>
                            {playlist.collaborative && (
                              <Badge variant="secondary" className="text-xs">
                                Collaborative
                              </Badge>
                            )}
                            {playlist.public && (
                              <Badge variant="outline" className="text-xs">
                                Public
                              </Badge>
                            )}
                          </div>
                          <div className="text-muted-foreground flex items-center space-x-2 text-sm">
                            <span>{playlist.tracksTotal} tracks</span>
                            {playlist.owner?.displayName && (
                              <>
                                <span>•</span>
                                <span>by {playlist.owner.displayName}</span>
                              </>
                            )}
                          </div>
                          {playlist.description && (
                            <p className="text-muted-foreground mt-1 line-clamp-1 text-sm">
                              {playlist.description}
                            </p>
                          )}
                        </div>
                        <div className="opacity-0 transition-opacity group-hover:opacity-100">
                          {isCurrentlyImporting ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <ArrowRight className="text-muted-foreground h-4 w-4" />
                          )}
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Confirmation Dialog */}
      {selectedPlaylist && (
        <Dialog open={!!selectedPlaylist} onOpenChange={() => setSelectedPlaylist(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Import Playlist</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="bg-muted flex items-center space-x-3 rounded-lg p-3">
                <img
                  src={getPlaylistImage(selectedPlaylist)}
                  alt={selectedPlaylist.name}
                  className="h-16 w-16 rounded-lg object-cover"
                />
                <div className="min-w-0 flex-1">
                  <h3 className="line-clamp-2 font-medium">{selectedPlaylist.name}</h3>
                  <p className="text-muted-foreground text-sm">
                    {selectedPlaylist.tracksTotal} tracks
                  </p>
                  {selectedPlaylist.owner?.displayName && (
                    <p className="text-muted-foreground text-sm">
                      by {selectedPlaylist.owner.displayName}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Playlist Name (optional)</label>
                <Input
                  placeholder={selectedPlaylist.name}
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                />
                <p className="text-muted-foreground text-xs">
                  Leave empty to use the original name from Spotify
                </p>
              </div>

              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedPlaylist(null)
                    setCustomName('')
                  }}
                  disabled={isImporting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleImport(selectedPlaylist)}
                  disabled={isImporting}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {isImporting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      Import Playlist
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}
